import { useState } from "react";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import axios from "axios";
import { ArrowRight, EnvelopeSimple, MapPin } from "@phosphor-icons/react";
import SectionLabel from "../components/SectionLabel";
import { BRAND, MEDIA, SERVICES } from "../lib/constants";
import { ease } from "../lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGETS = [
  "Under $25K",
  "$25K — $75K",
  "$75K — $200K",
  "$200K — $500K",
  "$500K+",
  "Not sure yet",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Message received. We'll be in touch within 24 hours.");
      setForm({
        name: "",
        email: "",
        company: "",
        service: "",
        budget: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Toaster
        theme="dark"
        position="top-center"
        toastOptions={{
          style: {
            background: "#0a0f1d",
            border: "1px solid rgba(212,175,55,0.4)",
            color: "#fff",
          },
        }}
      />

      {/* Hero */}
      <section className="relative pt-44 pb-12 md:pt-52 md:pb-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease }}
            className="max-w-4xl"
          >
            <p className="overline mb-6">Contact</p>
            <h1
              className="font-display tracking-tighter leading-[0.95] text-white"
              style={{ fontSize: "clamp(48px, 8vw, 128px)", fontWeight: 600 }}
              data-testid="contact-title"
            >
              Tell us what you're <br />
              <span className="text-[#d4af37] italic">building.</span>
            </h1>
            <p className="mt-8 text-lg text-[#a0abc0] max-w-xl leading-relaxed">
              A short note is enough to start. Every message is read by a
              partner — usually the same day, always within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form + Info */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Info */}
          <motion.aside
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease }}
            className="lg:col-span-5 order-2 lg:order-1"
          >
            <SectionLabel number="01" label="Studio details" />

            <div className="space-y-8 text-[#f8f9fa]">
              <div className="flex items-start gap-4">
                <span className="mt-1 flex items-center justify-center w-10 h-10 rounded-full border border-[#d4af37]/40 text-[#d4af37]">
                  <EnvelopeSimple size={18} />
                </span>
                <div>
                  <p className="overline mb-2">Email</p>
                  <a
                    href={`mailto:${BRAND.email}`}
                    data-testid="contact-email"
                    className="font-display text-2xl text-white hover:text-[#d4af37] transition-colors"
                  >
                    {BRAND.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <span className="mt-1 flex items-center justify-center w-10 h-10 rounded-full border border-[#d4af37]/40 text-[#d4af37]">
                  <MapPin size={18} />
                </span>
                <div>
                  <p className="overline mb-2">Headquarters</p>
                  <p className="text-white text-lg max-w-xs leading-relaxed">
                    {BRAND.address}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 rounded-2xl overflow-hidden border border-white/10 h-[260px] relative">
              <img
                src={MEDIA.contactBg}
                alt=""
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050814]/80 to-[#050814]/20" />
              <div className="absolute bottom-5 left-6 text-white">
                <p className="font-mono text-xs text-[#d4af37] tracking-widest">
                  EVO HEADQUARTERS
                </p>
                <p className="font-display text-lg mt-1">Hazratganj, Lucknow</p>
              </div>
            </div>
          </motion.aside>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease }}
            className="lg:col-span-7 order-1 lg:order-2"
          >
            <div className="rounded-2xl border border-white/10 bg-[#0a0f1d] p-8 md:p-12 relative overflow-hidden">
              <div
                aria-hidden
                className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-[#d4af37]/5 blur-3xl"
              />

              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.7, ease }}
                  className="relative py-12 text-center"
                  data-testid="contact-success"
                >
                  <div className="w-16 h-16 mx-auto rounded-full border border-[#d4af37] flex items-center justify-center text-[#d4af37] font-display text-3xl">
                    ✓
                  </div>
                  <h2 className="font-display text-3xl md:text-4xl text-white mt-8 tracking-tight">
                    Thank you.
                  </h2>
                  <p className="mt-4 text-[#a0abc0] max-w-md mx-auto leading-relaxed">
                    Your message reached a real human at Evo. We'll respond
                    within 24 hours.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    data-testid="contact-send-another"
                    className="inline-flex items-center gap-2 mt-8 text-[#d4af37] border-b border-[#d4af37] pb-1 hover:gap-4 transition-all"
                  >
                    Send another message <ArrowRight size={16} weight="bold" />
                  </button>
                </motion.div>
              ) : (
                <form
                  onSubmit={submit}
                  className="relative space-y-2"
                  data-testid="contact-form"
                >
                  <p className="overline mb-6">Project brief</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                    <div>
                      <label className="overline">Your name</label>
                      <input
                        type="text"
                        className="evo-input"
                        placeholder="Alex Chen"
                        value={form.name}
                        onChange={update("name")}
                        data-testid="contact-name"
                        required
                      />
                    </div>
                    <div>
                      <label className="overline">Email</label>
                      <input
                        type="email"
                        className="evo-input"
                        placeholder="alex@company.com"
                        value={form.email}
                        onChange={update("email")}
                        data-testid="contact-email-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="overline">Company</label>
                      <input
                        type="text"
                        className="evo-input"
                        placeholder="Halcyon Retail"
                        value={form.company}
                        onChange={update("company")}
                        data-testid="contact-company"
                      />
                    </div>
                    <div>
                      <label className="overline">Service interest</label>
                      <select
                        className="evo-input bg-[#0a0f1d]"
                        value={form.service}
                        onChange={update("service")}
                        data-testid="contact-service"
                      >
                        <option value="">Select a service</option>
                        {SERVICES.map((s) => (
                          <option key={s.id} value={s.title}>
                            {s.title}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="overline">Approximate budget</label>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {BUDGETS.map((b) => (
                          <button
                            type="button"
                            key={b}
                            onClick={() => setForm({ ...form, budget: b })}
                            data-testid={`budget-${b}`}
                            className={`text-xs font-mono px-4 py-2 rounded-full border transition-all ${
                              form.budget === b
                                ? "border-[#d4af37] bg-[#d4af37]/10 text-[#d4af37]"
                                : "border-white/10 text-[#a0abc0] hover:border-white/30 hover:text-white"
                            }`}
                          >
                            {b}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="md:col-span-2 mt-4">
                      <label className="overline">Tell us about the project</label>
                      <textarea
                        className="evo-input"
                        placeholder="A few sentences are enough. We'll ask the follow-up questions."
                        rows={5}
                        value={form.message}
                        onChange={update("message")}
                        data-testid="contact-message"
                        required
                      />
                    </div>
                  </div>

                  <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <p className="text-xs text-[#64748b] font-mono max-w-xs leading-relaxed">
                      We respond within 24 hours. We never share your details.
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit"
                      className="group inline-flex items-center gap-3 bg-[#d4af37] hover:bg-[#f2e3c6] disabled:opacity-60 disabled:cursor-not-allowed text-[#050814] font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:gap-5"
                    >
                      {submitting ? "Sending..." : "Send message"}
                      <ArrowRight size={18} weight="bold" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
